/**
 * @file interpret/engine/endpoints.ts
 * @brief Where the interpreter socket lives, and where a turn's TTS audio is.
 *
 * @details
 * Socket URL. The backend attaches its WebSocket server to the API host with no
 * path filter, and reads the JWT from `?token=` (browsers cannot set headers on a
 * WebSocket). Prod: the webpanel calls the API cross-origin
 * (`VITE_API_BASE_URL` = the Cloud Run URL), so the socket goes there too.
 * Dev: `VITE_API_BASE_URL` is empty and Vite proxies `/ws` to the backend
 * (`vite.config.ts`).
 *
 * Playback source. Port of mobile `features/s2sPlayback.ts`: inline base64 from
 * the `final` message first (no network), then the signed URL, then the API's
 * RAM proxy, which needs the Bearer header and so cannot go in an `<audio src>`.
 *
 * @author DES
 * @date 01-10-2026
 */

export function buildInterpretWsUrl(apiBaseUrl: string, token: string, pageOrigin: string): string {
  const base = apiBaseUrl
    ? apiBaseUrl.replace(/\/+$/, "").replace(/\/api$/, "")
    : pageOrigin.replace(/\/+$/, "");
  const wsBase = base.replace(/^http(s?):\/\//i, (_m, s: string) => `ws${s}://`);
  return `${wsBase}/ws?token=${encodeURIComponent(token)}`;
}

export type TurnAudioInput = {
  audioB64?: string | null;
  audioMime?: string | null;
  audioUrl?: string | null;
  objectName?: string | null;
  conversationId?: string | null;
};

export type TurnAudioSource =
  | { kind: "inline"; mime: string; base64: string }
  | { kind: "url"; url: string }
  | { kind: "proxy"; url: string };

export function resolveTurnAudio(
  input: TurnAudioInput,
  apiBaseUrl: string,
): TurnAudioSource | null {
  if (input.audioB64) {
    const raw = (input.audioMime || "audio/mpeg").replace(/^audio\//i, "");
    const sub = raw === "mp3" || raw === "" ? "mpeg" : raw;
    return { kind: "inline", mime: `audio/${sub}`, base64: input.audioB64 };
  }
  if (input.audioUrl) {
    // The server's own fallback URL is the authenticated proxy.
    return /\/api\/uploads\/read\//.test(input.audioUrl)
      ? { kind: "proxy", url: input.audioUrl }
      : { kind: "url", url: input.audioUrl };
  }
  const m = (input.objectName || "").match(/^sessions\/([^/]+)\/(.+)$/);
  if (m) {
    const conv = input.conversationId || m[1] || "";
    const base = apiBaseUrl.replace(/\/+$/, "");
    return {
      kind: "proxy",
      url: `${base}/api/uploads/read/${encodeURIComponent(conv)}/${encodeURIComponent(m[2] ?? "")}`,
    };
  }
  return null;
}
